import {
  shSampleSpreadMaxMm,
  type ShSampleSpreadCheck,
} from './internal/shSampleSpread'
import {
  formatShVboxRangeMm,
  type ShVboxRangeCheck,
} from './internal/shVboxRange'
import type {
  MeasurementFlowMessages,
  MeasurementFlowMessagesOverride,
} from './types'

// Phase guidance texts live in `@ui/features/measurement/Messages` (i18n
// `measurement:app.messages.*`); only the review/footer strings remain here.
const baseMessages: MeasurementFlowMessages = {
  complete: 'Measurement Complete',
  failedTitle: 'Measurement Failed',
  failed: 'Please try again, or close and start over.',
  left: 'Left',
  right: 'Right',
  total: 'Total PD',
  adjust: 'Adjust Segment Height',
  resetAdjustments: 'Reset Adjustments',
  adjustmentFailed: 'Could not adjust measurement',
  resetFailed: 'Could not reset measurement',
  cancel: 'Cancel',
  close: 'Close',
  retry: 'Try Again',
  confirm: 'Use Measurement',
  instructions: 'Instructions',
}

/**
 * Untranslated fallback for `ShMeasurementFlowProps.formatOutOfRangeWarning`.
 * Apps pass a localized formatter; this only keeps the package usable on its
 * own, so it does not attempt locale-aware number formatting.
 */
export const defaultOutOfRangeWarning = ({
  minMm,
  maxMm,
}: ShVboxRangeCheck): string =>
  `Outside the ${formatShVboxRangeMm(minMm)}–${formatShVboxRangeMm(
    maxMm
  )} mm range this frame allows. Check the fit, adjust manually, or try a different frame.`

/**
 * Untranslated fallback for `ShMeasurementFlowProps.formatUnstableWarning`.
 * One decimal always earns its place here — the spreads this fires on are
 * fractions of a millimetre apart — so there is no whole-number trim.
 */
export const defaultUnstableWarning = (spread: ShSampleSpreadCheck): string => {
  const worstMm = shSampleSpreadMaxMm(spread).toFixed(1)

  return `The frames in this capture disagreed by ${worstMm} mm. Hold still and repeat the measurement for a steadier result.`
}

export const resolvePdMessages = (
  override?: MeasurementFlowMessagesOverride
): MeasurementFlowMessages => ({
  ...baseMessages,
  ...override,
})

export const resolveShMessages = (
  override?: MeasurementFlowMessagesOverride
): MeasurementFlowMessages => ({
  ...baseMessages,
  ...override,
})
